// import React from 'react'
import * as Yup from 'yup'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormikProps } from 'formik'
import { useSelector } from 'react-redux'

import { DeclarationItemState, FullDeclaration, MemberReturn, StepProps } from '../../../types'
import { ActionButtonLook, ActionButtonVariant } from '../../../components/ActionButton/styled'
import { Icons } from '../../../components/Icon'
import { actions, selectors } from '../../../redux'
import DeclarationEntry from '../../../components/DeclarationEntry'
import {
  formatDeclarationItem,
  fullDeclarationToDeclaration,
} from '../../../helpers/DeclarationHelpers'
import { DeclarationItemType } from '../../../graphql/generated/graphql'
import { useSagaTakeLeading } from '../../../hooks/useSaga'

type AddDeclarationStepProps = StepProps & MemberReturn

export const useAddDeclarationStep = (props: AddDeclarationStepProps): any => {
  const {
    t,
    dispatch,
    declarations,
    addDeclaration,
    updateDeclaration,
    deleteDeclaration,
    addDeclarationStepPartText,
    addDeclarationLabel,
  } = props
  const [isAddingDeclaration, setIsAddingDeclaration] = useState(declarations.length ? false : true)
  const [sirenValue, setSirenValue] = useState('')
  const [index, setIndex] = useState(-1)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [addressValue, setAddressValue] = useState('')
  const [isEditingDeclaration, setIsEditingDeclaration] = useState(false)
  const [currentDeclarationItem, setCurrentDeclarationItem] = useState<FullDeclaration | null>(null)
  const [isHiddenfields, setIsHiddenfields] = useState<boolean>(true)
  const [isSirenEmpty, setIsSirenEmpty] = useState(false)
  // const [sirenError, setSirenError] = useState<string | undefined>(undefined)
  const isValidateSirenOrRnaSuccess = useSelector(selectors.declaration.validateSirenOrRna)
  const [declarationTypeValue, setDeclarationTypeValue] = useState(
    isEditingDeclaration ? currentDeclarationItem?.type : DeclarationItemType.Participation
  )
  const [helpText, setHelpText] = useState('')
  const formRef = useRef<FormikProps<any>>(undefined)
  const initialValues = useMemo(
    () => ({
      type: isEditingDeclaration ? currentDeclarationItem?.type : DeclarationItemType.Participation,
      companyJob: isEditingDeclaration ? currentDeclarationItem?.companyJob : '',
      siren: isEditingDeclaration ? currentDeclarationItem?.siren : '',
      companyName: isEditingDeclaration ? currentDeclarationItem?.companyName : '',
      entity: isEditingDeclaration ? currentDeclarationItem?.entity : '',
      address: isEditingDeclaration ? currentDeclarationItem?.address : '',
      city: isEditingDeclaration ? currentDeclarationItem?.city : '',
      postalCode: isEditingDeclaration ? currentDeclarationItem?.postalCode : '',
    }),
    [currentDeclarationItem, isEditingDeclaration]
  )

  useEffect(() => {
    const regexRnaOrSiren = /^(?:\d{9}|W\d{9})$/ // SIREN ou RNA
    const regexSiren = /^\d{9}$/ // Siren

    const hasEmptySiren =
      declarations.length <= 0 ||
      declarations.some((item) => {
        if (item.type === 'FOREIGN_COMPANY') {
          return false // Ne vérifie pas le SIREN/RNA pour les sociétés étrangères
        }

        if (item.type === 'MANDATE') {
          // Si c'est un mandat, il faut un SIREN ou un RNA valide
          return (
            typeof item.siren === 'undefined' ||
            !regexRnaOrSiren.test(item.siren) ||
            item.siren === '' ||
            item.siren === null
          )
        }

        // Pour les autres types, il faut obligatoirement un SIREN
        return (
          typeof item.siren === 'undefined' ||
          !regexSiren.test(item.siren) ||
          item.siren === '' ||
          item.siren === null
        )
      })

    setIsSirenEmpty(hasEmptySiren)
  }, [declarations])

  useEffect(() => {
    setHelpText(t('declaration.form.addDeclaration.addForm.type.help.participation'))
  }, [t])

  const options = useMemo(
    () => [
      {
        value: DeclarationItemType.Participation,
        help: t('declaration.form.addDeclaration.addForm.type.help.participation'),
      },
      {
        value: DeclarationItemType.Function,
        help: t('declaration.form.addDeclaration.addForm.type.help.fonction'),
      },
      {
        value: DeclarationItemType.Mandate,
        help: t('declaration.form.addDeclaration.addForm.type.help.mandat'),
      },
      {
        value: DeclarationItemType.ForeignCompany,
        help: t('declaration.form.addDeclaration.addForm.type.help.entreprise'),
      },
    ],
    [t]
  )

  const handleTypeChange = useCallback(
    (selectedType: DeclarationItemType) => {
      setSirenValue('')
      formRef?.current?.setFieldValue('siren', '')

      // Mettre à jour le texte d'aide en fonction de l'option sélectionnée
      const selectedOption = options.find((option) => option.value === selectedType)
      setHelpText(
        selectedOption
          ? selectedOption.help
          : t('declaration.form.addDeclaration.addForm.type.help')
      )
    },
    [options, t]
  )

  const declarationValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        type: Yup.string().required(),
        siren: Yup.string()
          .nullable()
          .when('type', {
            is: (type: DeclarationItemType) => type === DeclarationItemType.ForeignCompany,
            then: (schema) => schema.nullable(),
            otherwise: (schema) =>
              schema.when('type', {
                is: (type: DeclarationItemType) => type === DeclarationItemType.Mandate,
                then: (s) =>
                  s
                    .matches(/^(?:\d{9}|W\d{9})$/, t('siren.validation.invalid'))
                    .required(t('siren.validation.required')),
                otherwise: (s) =>
                  s
                    .matches(/^\d{9}$/, t('siren.validation.invalid'))
                    .required(t('siren.validation.required')),
              }),
          }),
        companyName: Yup.string().when('type', {
          is: DeclarationItemType.Participation,
          then: (schema) => schema.required(),
        }),
        entity: Yup.string().when('type', {
          is: DeclarationItemType.Mandate,
          then: (schema) => schema.required(),
        }),
        companyJob: Yup.string().required(),
        city: Yup.string().required(),
        address: Yup.string().required(),
        postalCode: Yup.string().required(),
      }),
    [t]
  )

  useSagaTakeLeading(
    actions.declaration.validateSirenOrRnaError,
    useCallback(() => {
      formRef.current?.setFieldTouched('siren', true)
      formRef.current?.setFieldError(
        'siren',
        t('declaration.form.addDeclaration.addForm.siren.error')
      )
    }, [t])
  )

  useSagaTakeLeading(
    actions.declaration.validateSirenOrRnaSuccess,
    useCallback(
      ({ payload }) => {
        if (!payload?.address || !payload?.city || !payload?.postalCode) {
          formRef.current?.setFieldTouched('siren', true)
          formRef.current?.setFieldError(
            'siren',
            t('declaration.form.addDeclaration.addForm.siren.error')
          )
          formRef.current?.setFieldValue('address', '')
          formRef.current?.setFieldValue('city', '')
          formRef.current?.setFieldValue('postalCode', '')
          formRef.current?.setFieldValue('companyName', '')
        } else {
          formRef.current?.setFieldValue('address', payload.address)
          formRef.current?.setFieldValue('city', payload.city)
          formRef.current?.setFieldValue('postalCode', payload.postalCode)
          if (declarationTypeValue !== DeclarationItemType.Mandate) {
            formRef.current?.setFieldValue('companyName', payload.companyName)
          } else {
            formRef.current?.setFieldValue('entity', payload.companyName)
          }
        }

        actions.declaration.validateSirenOrRnaReset()
      },
      [declarationTypeValue, t]
    )
  )

  useEffect(() => {
    return () => {
      dispatch(actions.declaration.validateSirenOrRnaReset())
    }
  }, [dispatch])

  const handleForm = useCallback(
    (values: any) => {
      switch (values.type) {
        case DeclarationItemType.Participation:
        case DeclarationItemType.Function:
          if (isEditingDeclaration) {
            updateDeclaration(currentDeclarationItem?.id as number, formRef.current?.values)
            setIsEditingDeclaration(false)
          } else {
            addDeclaration(values)
            setIsAddingDeclaration(false)
          }
          dispatch(actions.declaration.validateSirenOrRnaReset())
          setSirenValue('')
          formRef.current?.resetForm()
          break
        case DeclarationItemType.ForeignCompany:
        case DeclarationItemType.Mandate:
          if (isEditingDeclaration) {
            updateDeclaration(currentDeclarationItem?.id as number, values)
            setIsEditingDeclaration(false)
          } else {
            addDeclaration(values)
            setIsAddingDeclaration(false)
          }
          formRef.current?.resetForm()
          break
      }
    },
    [addDeclaration, isEditingDeclaration, updateDeclaration, currentDeclarationItem, dispatch]
  )

  useEffect(() => {
    if ((isAddingDeclaration || isEditingDeclaration) && sirenValue && sirenValue.length >= 9) {
      dispatch(actions.declaration.validateSirenOrRnaRequest({ sirenOrRna: sirenValue }))
    }
  }, [sirenValue, dispatch, isEditingDeclaration, isAddingDeclaration])

  useEffect(() => {
    if (declarationTypeValue === DeclarationItemType.ForeignCompany) {
      setIsHiddenfields(false)
    } else {
      setIsHiddenfields(true)
    }
  }, [declarationTypeValue])

  const onContextUpdate = useCallback((context: FormikProps<any>) => {
    setSirenValue(context.values?.siren)
    setDeclarationTypeValue(context.values?.type)
    setAddressValue(context.values?.address)
  }, [])

  const handleDeleteDeclaration = useCallback(
    (index: number) => {
      deleteDeclaration(index)
      setIsOpenModal(false)
    },
    [deleteDeclaration]
  )

  const addDeclarationButtonProps = useMemo(() => {
    return {
      field: {
        text: t('declaration.form.addDeclaration.add.label'),
        variant: ActionButtonVariant.secondary,
        iconProps: {
          icon: Icons.add,
        },
        iconPosition: 'left',
        onClick: () => {
          setIsAddingDeclaration(true)
        },
      },
      type: 'button',
    }
  }, [t])

  const addDeclarationFormProps = useMemo(() => {
    return {
      field: {
        shouldResetForm: false,
        title: {
          text: t('declaration.form.addDeclaration.addForm.title', {
            index: isEditingDeclaration
              ? (currentDeclarationItem?.id ?? 0) + 1
              : declarations.length + 1,
          }),
        },
        fields: {
          type: {
            label: t('declaration.form.addDeclaration.addForm.type.label'),
            help: helpText,
            onChange: (event: { target: { value: any } }) => {
              const selectedType = event.target.value
              setDeclarationTypeValue(selectedType)
              handleTypeChange(selectedType)
            },
            name: 'type',
            options: [
              {
                label: t('declaration.form.addDeclaration.addForm.type.option.participation'),
                value: DeclarationItemType.Participation,
              },
              {
                label: t('declaration.form.addDeclaration.addForm.type.option.function'),
                value: DeclarationItemType.Function,
              },
              {
                label: t('declaration.form.addDeclaration.addForm.type.option.mandate'),
                value: DeclarationItemType.Mandate,
              },
              {
                label: t('declaration.form.addDeclaration.addForm.type.option.company'),
                value: DeclarationItemType.ForeignCompany,
              },
            ],
          },
          companyJob: {
            name: 'companyJob',
            label: t('declaration.form.addDeclaration.addForm.companyJob.label'),
            placeholder: t('declaration.form.addDeclaration.addForm.companyJob.placeholder'),
            labelVariant: 'black',
            required: true,
          },
          siren: {
            name: 'siren',
            label:
              declarationTypeValue !== DeclarationItemType.Mandate
                ? t('declaration.form.addDeclaration.addForm.siren.label')
                : t('declaration.form.addDeclaration.addForm.sirenRna.label'),
            placeholder:
              declarationTypeValue !== DeclarationItemType.Mandate
                ? t('declaration.form.addDeclaration.addForm.siren.placeholder')
                : t('declaration.form.addDeclaration.addForm.sirenRna.placeholder'),
            labelVariant: 'black',
            required: true,
          },
          ...((isValidateSirenOrRnaSuccess && addressValue && addressValue.length !== 0) ||
          !isHiddenfields
            ? {
                companyName: {
                  name: 'companyName',
                  label: t('declaration.form.addDeclaration.addForm.companyName.label'),
                  placeholder: t('declaration.form.addDeclaration.addForm.companyName.placeholder'),
                  labelVariant: 'black',
                  required: true,
                },
                entity: {
                  name: 'entity',
                  label: t('declaration.form.addDeclaration.addForm.entity.label'),
                  placeholder: t('declaration.form.addDeclaration.addForm.entity.placeholder'),
                  labelVariant: 'black',
                  required: true,
                },
                address: {
                  name: 'address',
                  label: t('declaration.form.addDeclaration.addForm.address.label'),
                  placeholder: t('declaration.form.addDeclaration.addForm.address.placeholder'),
                  labelVariant: 'black',
                  required: true,
                },
                city: {
                  name: 'city',
                  label: t('declaration.form.addDeclaration.addForm.city.label'),
                  placeholder: t('declaration.form.addDeclaration.addForm.city.placeholder'),
                  labelVariant: 'black',
                  required: true,
                },
                postalCode: {
                  name: 'postalCode',
                  label: t('declaration.form.addDeclaration.addForm.postalCode.label'),
                  placeholder: t('declaration.form.addDeclaration.addForm.postalCode.placeholder'),
                  labelVariant: 'black',
                  required: true,
                },
              }
            : {}),
        },
        isInEditMode: isEditingDeclaration,
        submitButton: {
          text: isEditingDeclaration
            ? t('declaration.form.addDeclaration.editForm.submit.label')
            : t('declaration.form.addDeclaration.addForm.submit.label'),
          variant: isEditingDeclaration ? ActionButtonVariant.white : ActionButtonVariant.secondary,
          onClick: (values: any) => {
            formRef.current?.validateForm().then((errors) => {
              if (!errors || Object.keys(errors).length === 0) {
                handleForm(values)
                setIsSirenEmpty(false)
              }
            })
          },
        },
        cancelButton: {
          text: isEditingDeclaration
            ? t('declaration.form.addDeclaration.editForm.cancel.label')
            : t('declaration.form.addDeclaration.addForm.cancel.label'),
          variant: ActionButtonVariant.white,
          look: isEditingDeclaration ? ActionButtonLook.outlined : ActionButtonLook.contained,
          onClick: () => {
            if (isEditingDeclaration) {
              setIsEditingDeclaration(false)
              setCurrentDeclarationItem(null)
              return
            }
          },
        },
        ...(!isEditingDeclaration && {
          closeButton: {
            text: t('declaration.form.addDeclaration.addForm.close.label'),
            variant: ActionButtonVariant.white,
            look: isEditingDeclaration ? ActionButtonLook.outlined : ActionButtonLook.contained,
            onClick: () => {
              setIsEditingDeclaration(false)
              setIsAddingDeclaration(false)
              setCurrentDeclarationItem(null)
              return
            },
          },
        }),
      },
      type: 'declarationEntry',
    }
  }, [
    addressValue,
    currentDeclarationItem?.id,
    declarationTypeValue,
    declarations.length,
    handleForm,
    handleTypeChange,
    helpText,
    isEditingDeclaration,
    isHiddenfields,
    isValidateSirenOrRnaSuccess,
    t,
  ])

  const declarationTableProps = useMemo(() => {
    return {
      field: {
        numberHeaderLabel: t('declaration.form.addDeclaration.declarationTable.numberHeaderLabel'),
        descriptionHeaderLabel: t(
          'declaration.form.addDeclaration.declarationTable.descriptionHeaderLabel'
        ),
        actionHeaderLabel: t('declaration.form.addDeclaration.declarationTable.actionHeaderLabel'),
        stateHeaderLabel: t('declaration.form.addDeclaration.declarationTable.stateHeaderLabel'),
        refusedStateLabel: t('declaration.form.addDeclaration.declarationTable.refusedStateLabel'),
        validatedStateLabel: t(
          'declaration.form.addDeclaration.declarationTable.validatedStateLabel'
        ),
        pendingStateLabel: t('declaration.form.addDeclaration.declarationTable.pendingStateLabel'),
        editingStateLabel: t('declaration.form.addDeclaration.declarationTable.editingStateLabel'),
        declarations: (declarations as FullDeclaration[])?.map((item, index) => ({
          id: index + 1,
          description: formatDeclarationItem(t, fullDeclarationToDeclaration(item)),
          state: DeclarationItemState.Validated,
          isEditing: isEditingDeclaration && currentDeclarationItem?.id === index,
          editingForm: <DeclarationEntry {...addDeclarationFormProps.field} />,
          actions: {
            delete: {
              variant: ActionButtonVariant.secondary,
              look: ActionButtonLook.outlined,
              iconProps: {
                icon: Icons.trash,
              },
              onClick: () => {
                setIndex(index)
                setIsOpenModal(true)
              },
            },
            modify: {
              text: t('declaration.form.addDeclaration.editForm.edit.label'),
              noTextOnMobile: true,
              noIconOnDesktop: true,
              variant: ActionButtonVariant.secondary,
              look: ActionButtonLook.contained,
              iconProps: {
                icon: Icons.edit,
              },
              onClick: () => {
                setIsEditingDeclaration(true)
                setCurrentDeclarationItem({
                  ...item,
                  id: index,
                })
              },
            },
          },
        })),
        erro: 'test',
      },
      type: 'declarationTable',
    }
  }, [
    addDeclarationFormProps.field,
    currentDeclarationItem?.id,
    declarations,
    isEditingDeclaration,
    t,
  ])

  return {
    stepPart: {
      text: addDeclarationStepPartText,
    },
    label: addDeclarationLabel,
    form: {
      innerRef: formRef,
      initialValues,
      validationSchema:
        isEditingDeclaration || isAddingDeclaration || declarations.length === 0
          ? declarationValidationSchema
          : undefined,
      onContextUpdate,
      onSubmit: () => {
        if (!isEditingDeclaration && !isAddingDeclaration && !isSirenEmpty) {
          dispatch(actions.declaration.setNextStep())
        }
        // if (isSirenEmpty) {
        //   setSirenError(t('declaration.form.confirmation.siren.single'))
        // }
      },
      validateOnChange: true,
      enableReinitialize: true,
    },
    fields: [
      ...(declarations?.length > 0 ? [declarationTableProps] : []),
      ...(isAddingDeclaration && !isEditingDeclaration ? [addDeclarationFormProps] : []),
      ...(!isAddingDeclaration && !isEditingDeclaration ? [addDeclarationButtonProps] : []),
    ],
    button: {
      text: t('declaration.form.addDeclaration.submit.label'),
      iconProps: {
        icon: Icons.arrowRight,
      },
      iconPosition: 'right',
      disabled: isSirenEmpty,
    },
    modalProps: {
      isOpen: isOpenModal,
      onClose: () => setIsOpenModal(false),
    },
    deleteModal: {
      title: t('declaration.form.addDeclaration.deleteModal.title', {
        fieldName:
          declarations[index]?.companyName ||
          declarations[index]?.entity ||
          t('declaration.form.addDeclaration.deleteModal.unknown'),
      }),
      text: t('declaration.form.addDeclaration.deleteModal.text'),
      buttons: [
        {
          variant: ActionButtonVariant.whiteWithBorder,
          look: ActionButtonLook.contained,
          bigPadding: true,
          text: t('declaration.form.addDeclaration.deleteModal.cancel.label'),
          onClick: () => {
            setIsOpenModal(false)
          },
        },
        {
          variant: ActionButtonVariant.danger,
          look: ActionButtonLook.contained,
          bigPadding: true,
          text: t('declaration.form.addDeclaration.deleteModal.submit.label'),
          onClick: () => {
            handleDeleteDeclaration(index)
          },
          iconProps: {
            icon: Icons.trash,
          },
          iconPosition: 'right' as const,
        },
      ],
    },
  }
}
